<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1040px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span><span>Cobrar</span>
        </div>
        <h1 class="vx-titulo">Cobrar</h1>
        <div class="vx-ayuda mt-1">
          Elegí a quién se le cobra, qué {{ vocabulario.plural.toLowerCase() }} y por cuánto.
          El cobro se registra dentro de la caja abierta.
        </div>
      </div>

      <!-- 01 — A quién -->
      <div class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">01</span>
            <h2 class="vx-panel-titulo">A quién se le cobra</h2>
          </div>
          <button v-if="suscripcion" class="vx-btn vx-btn-sm vx-btn-texto" @click="limpiarSeleccion">
            Cambiar
          </button>
        </div>

        <div v-if="!suscripcion" class="p-3">
          <div class="position-relative" style="max-width: 460px">
            <input
              id="buscador-suscripciones"
              v-model.trim="busqueda"
              class="vx-campo"
              placeholder="Buscar por documento o nombre del responsable, o por nombre o identificador de la ficha…"
              @input="buscar"
            >
            <!-- CR-BC-003 · RB-3. Agrupado por responsable: cuando el padre
                 tiene tres hijos, el cajero veía tres filas seguidas con el
                 mismo apellido y tenía que leer letra por letra para saber si
                 eran hermanos. Con el encabezado, «cuál de los hijos» se
                 contesta solo.

                 `resultados-suscripciones` se conserva en el contenedor: hay
                 E2E que ya dependen de él. -->
            <div
              v-if="grupos.length"
              class="position-absolute w-100 mb-0 vx-panel"
              data-test="resultados-suscripciones"
              style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 300px; overflow-y: auto"
            >
              <div v-for="g in grupos" :key="g.cliente_id ?? 'sin-responsable'">
                <div class="px-3 pt-2 pb-1 d-flex align-items-baseline gap-2"
                     style="background: var(--vx-fondo-sutil, #f7f7f8)"
                     data-test="grupo-responsable">
                  <span style="font-size: 12px; font-weight: 650">{{ g.nombre }}</span>
                  <!-- RB-4. El documento distingue a dos homónimos, y es lo que
                       el cajero tiene delante para comparar. -->
                  <span v-if="g.documento" class="vx-celda-sub vx-num">{{ g.documento }}</span>
                  <span v-else class="vx-celda-sub">sin documento</span>
                </div>
                <ul class="list-unstyled mb-0">
                  <li
                    v-for="s in g.fichas"
                    :key="s.cr_suscripcion_id"
                    class="px-3 py-2 border-bottom"
                    style="cursor: pointer"
                    data-test="ficha-opcion"
                    @click="elegir(s)"
                  >
                    <div style="font-size: 13px; font-weight: 550">
                      {{ nombreDe(s) }}
                      <!-- AI-A2 (H-AI). Aparece acá porque dar de baja
                           CONSERVA las cuotas con pagos aplicados (D6), así
                           que una suscripción cerrada puede seguir debiendo.
                           La marca no es decorativa: sin ella el cajero cobra
                           creyendo que la suscripción está vigente. -->
                      <span
                        v-if="s.estado && s.estado !== 'ACTIVA'"
                        class="vx-estado vx-estado-neutro ms-1"
                        data-test="ficha-no-activa"
                      >{{ s.estado === 'FINALIZADA' ? 'dada de baja' : 'suspendida' }}</span>
                    </div>
                    <div class="vx-celda-sub">{{ identificadorDe(s) || s.esquema_nombre }}</div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="vx-ayuda mt-2">
            Escribí al menos dos caracteres. Buscá por documento o nombre del
            responsable, o por nombre o identificador de la ficha.
          </div>
        </div>

        <div v-else class="p-3 d-flex align-items-center gap-3" data-test="suscripcion-elegida">
          <div class="vx-iniciales">{{ iniciales(nombreDe(suscripcion)) }}</div>
          <div>
            <div style="font-size: 14px; font-weight: 600">{{ nombreDe(suscripcion) }}</div>

            <!-- D43 · RN-126. El pagador se elige y se cambia ACÁ, junto a las
                 cuotas, que es donde el operador está mirando. Con un solo
                 candidato sigue siendo la línea de texto de siempre: un
                 desplegable de un elemento es ruido. -->
            <div v-if="hayEleccionDePagador" class="d-flex align-items-center gap-2 flex-wrap">
              <span class="vx-ayuda">Paga</span>
              <select
                v-model.number="pagadorId" class="vx-campo vx-campo-sm"
                style="width: auto; min-width: 190px" data-test="selector-pagador"
              >
                <option v-for="t in candidatos" :key="t.cliente_id" :value="Number(t.cliente_id)">
                  {{ t.nombre_completo }}{{ t.rol === 'RESPONSABLE_PAGO' ? '' : ' (cotitular)' }}
                </option>
              </select>
              <span class="vx-ayuda">· {{ suscripcion.esquema_nombre }}</span>
            </div>
            <div v-else class="vx-ayuda" data-test="pagador-fijo">
              Paga {{ suscripcion.responsable_nombre || '—' }} · {{ suscripcion.esquema_nombre }}
            </div>
          </div>
        </div>
      </div>

      <!-- 02 — Qué cuotas -->
      <div v-if="suscripcion" class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">02</span>
            <h2 class="vx-panel-titulo">{{ vocabulario.plural }} a cobrar</h2>
          </div>
          <button v-if="pendientes.length" class="vx-btn vx-btn-sm vx-btn-texto" @click="alternarTodas">
            {{ todasElegidas ? 'Ninguna' : 'Todas' }}
          </button>
        </div>

        <div v-if="cargandoDeudas" class="vx-vacio">
          <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
          <span class="vx-vacio-texto">Cargando…</span>
        </div>

        <div v-else-if="!pendientes.length" class="vx-vacio" data-test="sin-pendientes">
          <i class="bi bi-check2-circle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
          <span class="vx-vacio-titulo">No hay nada pendiente</span>
          <span class="vx-vacio-texto">
            Esta suscripción no tiene {{ vocabulario.plural.toLowerCase() }} con saldo.
          </span>
        </div>

        <table v-else class="vx-tabla" data-test="tabla-pendientes">
          <thead>
            <tr>
              <th style="width: 36px"></th>
              <th>Concepto</th>
              <th>Vence</th>
              <th class="vx-col-num">Saldo</th>
              <th class="vx-col-num" style="width: 150px">A cobrar</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in pendientes" :key="d.cr_deuda_id">
              <td>
                <input
                  type="checkbox"
                  :checked="Boolean(elegidas[d.cr_deuda_id])"
                  :aria-label="`Cobrar ${d.descripcion}`"
                  @change="alternar(d)"
                >
              </td>
              <td>
                <div class="vx-celda-principal">{{ d.descripcion }}</div>
                <div class="vx-celda-sub vx-num">{{ d.periodo }}</div>
              </td>
              <td class="vx-num">
                {{ fechaCorta(d.fecha_vencimiento) }}
                <span v-if="d.vencida" class="vx-estado vx-estado-atencion ms-1">Vencida</span>
              </td>
              <td class="vx-col-num vx-num">{{ formatearMonto(d.saldo) }}</td>
              <td class="vx-col-num">
                <input
                  v-if="elegidas[d.cr_deuda_id]"
                  v-model.number="elegidas[d.cr_deuda_id].monto"
                  class="vx-campo vx-campo-sm vx-num text-end"
                  type="number" min="1" :max="Number(d.saldo)"
                  :class="{ 'vx-campo-error': errorDeLinea(d) }"
                  :data-test="`monto-${d.cr_deuda_id}`"
                >
                <span v-else class="vx-ayuda">—</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="errorMontos" class="px-3 pb-2">
          <div class="vx-error" data-test="error-montos">{{ errorMontos }}</div>
        </div>

        <!-- D43 · RN-126.1. La factura va a salir a nombre de otra persona.
             Es correcto y es el objetivo, pero no puede pasar sin que el
             cajero lo vea. No bloquea, igual que el aviso de abajo. -->
        <div v-if="pagaOtro" class="px-3 pb-2" data-test="aviso-pagador">
          <div class="vx-nota" style="border-left: 3px solid #1d4ed8">
            <i class="bi bi-person-check"></i>
            <span>
              El documento sale a nombre de <strong>{{ nombrePagador }}</strong>,
              no de {{ suscripcion.responsable_nombre }}.
              La deuda sigue siendo de {{ suscripcion.responsable_nombre }}.
            </span>
          </div>
        </div>

        <!-- DA-F5-005. No bloquea: informa. Cobrar de menos es válido; lo que
             no puede es pasar sin que nadie lo vea. -->
        <div
          v-if="cuotasConRemanente.length" class="px-3 pb-3"
          data-test="aviso-remanente"
        >
          <div class="vx-nota" style="border-left: 3px solid #b45309">
            <i class="bi bi-exclamation-triangle"></i>
            <span>
              Este cobro deja <strong>{{ formatearMonto(totalRemanente) }}</strong>
              debiendo en
              {{ cuotasConRemanente.length }}
              {{ cuotasConRemanente.length === 1 ? 'cuota' : 'cuotas' }}
              ({{ cuotasConRemanente.map((c) => c.periodo).join(', ') }}).
              Si querés aplicar un descuento, cargalo en la suscripción: cobrar
              de menos acá deja deuda.
            </span>
          </div>
        </div>
      </div>

      <!--
        03 — Productos (CR-E4-001, RN-34, RN-48).
        Aparece con la suscripción elegida y NO exige cuotas pendientes: el
        padre que viene sólo a comprar un uniforme es un cobro legítimo, y
        obligarlo a tener una cuota abierta lo mandaría a otra pantalla.
      -->
      <div v-if="suscripcion" class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">03</span>
            <h2 class="vx-panel-titulo">Productos</h2>
          </div>
          <span class="vx-ayuda">Opcional — uniformes, materiales</span>
        </div>
        <div class="p-3">
          <table v-if="productos.length" class="vx-tabla mb-3" data-test="tabla-productos">
            <thead>
              <tr>
                <th>Producto</th>
                <th class="vx-col-num" style="width: 110px">Cantidad</th>
                <th class="vx-col-num">Precio</th>
                <th class="vx-col-num">Importe</th>
                <th style="width: 44px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in productos" :key="p.producto_id">
                <td>
                  <div class="vx-celda-principal">{{ p.nombre }}</div>
                  <div class="vx-celda-sub vx-num">{{ p.codigo }}</div>
                </td>
                <td class="vx-col-num">
                  <input
                    v-model.number="p.cantidad" class="vx-campo vx-campo-sm vx-num text-end"
                    type="number" min="1" :max="p.stock"
                    :class="{ 'vx-campo-error': p.cantidad > p.stock || p.cantidad < 1 }"
                    :data-test="`cantidad-${p.producto_id}`"
                  >
                </td>
                <td class="vx-col-num vx-num">{{ formatearMonto(p.precio) }}</td>
                <td class="vx-col-num vx-num">{{ formatearMonto(p.precio * (p.cantidad || 0)) }}</td>
                <td>
                  <button class="vx-btn vx-btn-sm" title="Quitar" @click="productos.splice(i, 1)">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="position-relative" style="max-width: 420px">
            <input
              id="buscador-productos"
              v-model.trim="busquedaProducto"
              class="vx-campo vx-campo-sm"
              placeholder="Agregar producto por nombre o código…"
              @input="buscarProducto"
            >
            <ul
              v-if="resultadosProducto.length"
              class="list-unstyled position-absolute w-100 mb-0 vx-panel"
              data-test="resultados-productos"
              style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 260px; overflow-y: auto"
            >
              <li
                v-for="p in resultadosProducto" :key="p.producto_id"
                class="px-3 py-2 border-bottom" style="cursor: pointer"
                @click="agregarProducto(p)"
              >
                <div style="font-size: 13px; font-weight: 550">{{ p.nombre }}</div>
                <div class="vx-celda-sub vx-num">
                  {{ p.precio ? formatearMonto(p.precio) : 'Sin precio vigente' }}
                  · stock {{ p.stock }}
                </div>
              </li>
            </ul>
          </div>

          <div v-if="errorProductos" class="vx-error mt-2" data-test="error-productos">
            {{ errorProductos }}
          </div>

          <!--
            RN-42. El recibo de dinero no puede llevar bienes físicos: es un
            comprobante de PAGO firmado (Ley 6822/2021), no un comprobante de
            venta. Se avisa acá, con el producto ya cargado y antes de apretar
            Cobrar, en vez de dejar que el backend lo rechace al final.
          -->
          <div v-if="reciboConProductos" class="vx-nota mt-2" data-test="aviso-recibo-productos">
            <i class="bi bi-info-circle"></i>
            <span>
              Con <strong>recibo de dinero</strong>, el producto sale del stock y la
              venta queda <strong>pendiente de facturar</strong>: no se emite factura
              electrónica ahora, se agrupa después en una sola.
            </span>
          </div>
        </div>
      </div>

      <!-- 04 — Cómo -->
      <div v-if="suscripcion && hayAlgoQueCobrar" class="vx-panel mb-3">
        <div class="vx-panel-cabecera">
          <div class="d-flex align-items-baseline gap-2">
            <span class="vx-paso">04</span>
            <h2 class="vx-panel-titulo">Forma de pago y documento</h2>
          </div>
        </div>
        <div class="p-3">
          <div class="row g-3">
            <div class="col-md-4">
              <label class="vx-etiqueta" for="forma-pago">Forma de pago</label>
              <select id="forma-pago" v-model="formaPago" class="vx-campo">
                <option v-for="f in FORMAS_PAGO" :key="f.valor" :value="f.valor">{{ f.texto }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" for="documento-tipo">Documento</label>
              <select id="documento-tipo" v-model="documentoTipo" class="vx-campo">
                <option value="FACTURA">Factura electrónica</option>
                <option value="RECIBO">Recibo de dinero</option>
              </select>
              <div class="vx-ayuda mt-1">
                Preferencia de la suscripción: {{ suscripcion.documento_preferido === 'RECIBO' ? 'recibo' : 'factura' }}.
              </div>
            </div>
            <div class="col-md-4">
              <label class="vx-etiqueta" for="referencia">Referencia</label>
              <input id="referencia" v-model.trim="referencia" class="vx-campo" placeholder="Opcional">
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger py-2 px-3 mb-3" style="font-size: 13.5px" role="alert"
           data-test="error-cobro">
        {{ error }}
        <!-- El error de caja cerrada es el unico que tiene una salida de un
             solo paso, y el cajero la tiene a mano: abrir la caja. Sin este
             boton hay que irse a /caja y perder las cuotas ya marcadas. -->
        <div v-if="cajaCerrada && puedeAbrirCaja" class="mt-2">
          <button class="btn btn-success btn-sm" data-test="btn-abrir-caja" @click="mostrarApertura = true">
            <i class="bi bi-unlock me-1"></i>Abrir caja
          </button>
        </div>
        <div v-else-if="cajaCerrada" class="mt-2">
          Pedile a alguien con permiso de caja que abra la caja para poder cobrar.
        </div>
        <!-- RN-7 / RF-10. Tampoco es un error mas de la lista: el cobro es
             legitimo y lo que falta es un dato. El backend rechaza y PROPONE
             (no deriva solo a recibo, articulo 6), asi que la pantalla tiene
             que ofrecer las dos salidas reales sin perder lo ya marcado. -->
        <div v-else-if="faltaIdentificacionFiscal" class="mt-2 d-flex flex-wrap gap-2">
          <button class="btn btn-outline-primary btn-sm" data-test="btn-cobrar-con-recibo"
                  @click="cobrarConRecibo">
            <i class="bi bi-receipt me-1"></i>Cobrar con recibo de dinero
          </button>
          <!-- La ruta se llama `Clientes` y esta gateada con `ver_cliente`:
               sin el permiso, el boton llevaria a un rechazo del guard. Mejor
               no ofrecer una salida que no existe para ese usuario. -->
          <RouterLink v-if="puedeVerClientes" class="btn btn-outline-secondary btn-sm"
                      data-test="btn-cargar-identificacion"
                      :to="{ name: 'Clientes' }">
            <i class="bi bi-person-vcard me-1"></i>Cargar su RUC o cédula
          </RouterLink>
        </div>
      </div>

      <!-- Total y acción -->
      <div v-if="suscripcion && hayAlgoQueCobrar" class="vx-panel mb-3">
        <div class="p-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <div class="vx-ayuda">Total a cobrar</div>
            <div class="vx-num" style="font-size: 28px; font-weight: 680; letter-spacing: -0.02em"
                 data-test="total-cobro">
              {{ formatearMonto(total) }}
            </div>
            <div class="vx-ayuda" data-test="desglose-total">{{ desglose }}</div>
          </div>
          <button
            class="vx-btn vx-btn-primario"
            data-test="btn-cobrar"
            :disabled="cobrando"
            @click="confirmar"
          >
            <span v-if="cobrando" class="spinner-border spinner-border-sm"></span>
            {{ cobrando ? 'Cobrando…' : 'Cobrar' }}
          </button>
        </div>
      </div>

      <!-- Resultado -->
      <div v-if="resultado" class="vx-panel" data-test="panel-resultado">
        <div class="vx-panel-cabecera"><h2 class="vx-panel-titulo">Cobro registrado</h2></div>
        <div class="p-3">
          <div class="vx-aviso">
            <div class="vx-aviso-titulo">
              <i class="bi bi-check2-circle"></i>
              <span data-test="resultado-monto">{{ formatearMonto(resultado.monto_total) }} cobrados</span>
            </div>
            <div class="vx-ayuda mt-1">
              {{ resultado.aplicaciones?.length || 0 }} {{ vocabulario.plural.toLowerCase() }}
              <template v-if="resultado.productos?.length">
                · {{ resultado.productos.length }}
                producto{{ resultado.productos.length === 1 ? '' : 's' }}
              </template>
              · {{ resultado.documento_tipo === 'RECIBO' ? 'recibo de dinero' : 'factura electrónica' }}
            </div>
          </div>

          <!--
            CF16 / RN-42. Si la preferencia decía recibo y el cobro llevaba
            productos, el backend forzó factura. Callarlo dejaría al operador
            viendo una factura donde esperaba un recibo, sin saber por qué —
            y el silencio se lee como error del sistema.
          -->
          <div v-if="resultado.venta_diferida"
               class="alert alert-info py-2 px-3 mt-3 mb-0" style="font-size: 13px"
               data-test="aviso-venta-diferida">
            <i class="bi bi-clock-history"></i>
            El producto salió del stock y la venta quedó <strong>pendiente de
            facturar</strong>. No se emitió factura electrónica: se agrupa con las
            demás en una factura única.
          </div>

          <div v-if="resultado.documento_forzado === 'RECIBO_A_FACTURA'"
               class="alert alert-warning py-2 px-3 mt-3 mb-0" style="font-size: 13px"
               data-test="aviso-documento-forzado">
            <i class="bi bi-info-circle"></i>
            Esta suscripción prefiere recibo de dinero, pero el cobro incluye productos y
            el recibo no puede llevarlos: se emitió <strong>factura electrónica</strong>.
          </div>

          <!-- El estado de emisión se explica, no se muestra pelado: "pendiente"
               a secas hace que alguien intente arreglar algo que se arregla solo. -->
          <div class="d-flex align-items-start gap-2 mt-3">
            <span class="vx-estado" :class="emision.clase" data-test="estado-emision">{{ emision.texto }}</span>
            <span class="vx-ayuda" style="max-width: 560px">{{ emision.nota }}</span>
          </div>

          <!--
            Entregarle algo al pagador. Reusa `cashboxLocalPrintService`, que
            ya imprime comprobantes de movimiento de caja en IngresoVarios y
            CobroClienteCredito (artículo 1) — el cobro devuelve el
            `movimiento_id` que necesita.

            OJO CON QUÉ ES ESTO: el comprobante del MOVIMIENTO DE CAJA, no la
            factura electrónica ni el recibo de dinero firmado. Es la
            constancia de que se cobró. La factura se entrega por su KUDE.
          -->
          <!--
            Acá vivía un segundo panel con «Descargar PDF» y «Copiar enlace»
            del recibo, armados con la URL que devuelve facturación
            electrónica. Se fue entero (2026-09-06):

            1. entregaba el enlace equivocado — en producción esa base es el
               alias de la red Docker, que no resuelve fuera del servidor;
            2. duplicaba el número de recibo que ya dice el estado de emisión,
               justo encima;
            3. competía con «Entregar comprobante», que es el camino bueno:
               pide el enlace al backend, sale con la marca del comercio y es
               el MISMO que el operador va a encontrar mañana en el listado.

            Una sola forma de entregar, y que sea la que funciona.
          -->

          <!--
            E10. Entregar el comprobante DESDE ACÁ ya se podía; lo que no se
            podía era volver a hacerlo después. Este botón usa el mismo camino
            que el listado de cobros —pide el comprobante por id— así que lo
            que el operador ve acá es exactamente lo que va a encontrar mañana
            si el pagador se lo pide de nuevo.
          -->
          <div class="mt-3">
            <button class="vx-btn vx-btn-sm" data-test="btn-entregar-comprobante"
                    @click="comprobanteDe = resultado.cr_cobro_id">
              <i class="bi bi-box-arrow-up-right"></i> Entregar comprobante
            </button>
          </div>

          <div v-if="errorImpresion" class="vx-error mt-3" data-test="error-impresion">
            {{ errorImpresion }}
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button
              v-if="resultado.caja?.movimiento_id"
              class="vx-btn"
              data-test="btn-imprimir"
              :disabled="imprimiendo"
              @click="imprimirComprobante"
            >
              <span v-if="imprimiendo" class="spinner-border spinner-border-sm"></span>
              <i v-else class="bi bi-printer"></i>
              {{ imprimiendo ? 'Imprimiendo…' : 'Imprimir comprobante' }}
            </button>
            <button class="vx-btn" data-test="btn-otro-cobro" @click="reiniciar">Cobrar otra cosa</button>
            <router-link class="vx-btn" :to="{ name: 'CobranzaCobros' }">Ver cobros</router-link>
          </div>
        </div>
      </div>

      <ComprobanteEntrega
        :abierto="Boolean(comprobanteDe)"
        :cobro-id="comprobanteDe"
        @cerrado="comprobanteDe = null"
      />

      <AbrirCajaModal v-model:abierto="mostrarApertura" @abierta="alAbrirCaja" />

    </div>
  </div>
</template>

<script setup>
// Cobro dentro de una caja abierta — CR-E3-008.
// SPEC seccion 9 · CF6, CF7 · RN-24, RN-26, RN-30, RN-31.

import { ref, reactive, computed } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import ComprobanteEntrega from '@/components/ComprobanteEntrega.vue'
import AbrirCajaModal from '@/components/AbrirCajaModal.vue'
import { useNotificationStore } from '@/stores/notifications'
import { hasPermission } from '@/services/authSessionService'
import cashboxLocalPrintService from '@/services/cashboxLocalPrintService'
import localPrintSettingsService from '@/services/localPrintSettingsService'
import {
  listarSuscripciones, listarDeudas, cobrar, nuevaClaveIdempotencia,
  buscarProductos, obtenerSuscripcion, candidatosAPagador,
  obtenerVocabulario, VOCABULARIO_POR_DEFECTO, FORMAS_PAGO, estadoEmision,
  formatearMonto, fechaCorta, iniciales
} from '@/services/cobranzaService'

const notificaciones = useNotificationStore()

const busqueda = ref('')
const resultados = ref([])
const suscripcion = ref(null)
const deudas = ref([])
const cargandoDeudas = ref(false)
const elegidas = reactive({})
// D43 — quién paga. `candidatos` son los titulares vigentes que pueden pagar
// (RN-117); `pagadorId` arranca siempre en el responsable (RN-126).
const candidatos = ref([])
const pagadorId = ref(null)
const formaPago = ref('EFECTIVO')
const documentoTipo = ref('FACTURA')
const referencia = ref('')
const cobrando = ref(false)
const error = ref('')
const resultado = ref(null)

// Caja cerrada: no es un error mas de la lista, es el unico que la pantalla
// sabe resolver sin salir de si misma (RN-24 — sin caja abierta el backend no
// escribe nada).
const cajaCerrada = ref(false)
const mostrarApertura = ref(false)
const puedeAbrirCaja = hasPermission('abrir_caja')

// RN-7 (RF-10): al responsable le falta RUC o cedula. Como el de caja cerrada,
// es un rechazo con salida conocida — y son DOS, porque el operador puede no
// tener el documento a mano ahora mismo: cobrar con recibo, o ir a cargarlo.
const faltaIdentificacionFiscal = ref(false)
const puedeVerClientes = hasPermission('ver_cliente')

const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['deuda.singular'],
  plural: etiquetas.value['deuda.plural']
}))
obtenerVocabulario().then((v) => { etiquetas.value = v })

// La clave de idempotencia se genera UNA vez por intento y se conserva si el
// envío falla: es lo que hace que reintentar no cobre dos veces (RN-30).
let claveIdempotencia = nuevaClaveIdempotencia()

const pendientes = computed(() =>
  deudas.value.filter((d) => d.estado !== 'ANULADA' && Number(d.saldo) > 0)
)

const cantidadElegidas = computed(() => Object.keys(elegidas).length)
const todasElegidas = computed(() =>
  pendientes.value.length > 0 && cantidadElegidas.value === pendientes.value.length
)

const totalCuotas = computed(() =>
  Object.values(elegidas).reduce((acc, e) => acc + (Number(e.monto) || 0), 0)
)

// --- Productos en el cobro (CR-E4-001, RN-34, RN-48) ----------------------
//
// El precio que se ve acá es informativo: el que se cobra lo resuelve el
// backend contra el catálogo dentro de la transacción (RP-9). Se muestra
// igual porque el operador necesita decirle un total al que está pagando.

const productos = ref([])
const busquedaProducto = ref('')
const resultadosProducto = ref([])
let temporizadorProducto

const totalProductos = computed(() =>
  productos.value.reduce((acc, p) => acc + (Number(p.precio) || 0) * (Number(p.cantidad) || 0), 0)
)

const total = computed(() => totalCuotas.value + totalProductos.value)

// RN-126: el selector sólo tiene sentido con más de un candidato.
const hayEleccionDePagador = computed(() => candidatos.value.length > 1)

const pagadorElegido = computed(() =>
  candidatos.value.find((t) => Number(t.cliente_id) === Number(pagadorId.value)) || null
)

// RN-126.1 — el aviso. No bloquea: se ve. Emitir una factura a nombre de otra
// persona es correcto y es el objetivo, pero no puede pasar sin que el cajero
// lo note. Mismo criterio que el aviso de remanente de DA-F5-005.
const pagaOtro = computed(() =>
  Boolean(suscripcion.value)
  && pagadorId.value != null
  && Number(pagadorId.value) !== Number(suscripcion.value.responsable_cliente_id)
)

const nombrePagador = computed(() =>
  pagadorElegido.value?.nombre_completo
  || suscripcion.value?.responsable_nombre
  || null
)

// DA-F5-005 — el aviso que faltaba el 2026-09-11.
//
// La validación de línea impide cobrar MÁS que el saldo; cobrar MENOS pasa en
// silencio, porque es pago parcial, una función legítima (RN-26).
//
// **Ese silencio fue la tercera falla del caso de producción.** La beca no se
// había aplicado, el cajero compensó cobrando 900.000 sobre un saldo de
// 1.000.000, y el sistema dejó 100.000 debiendo sin decir nada: la deuda terminó
// contradiciendo una factura ya aprobada en SIFEN.
//
// No bloquea —cobrar de menos sigue siendo válido— pero se ve antes de confirmar.
const cuotasConRemanente = computed(() =>
  pendientes.value
    .filter((d) => elegidas[d.cr_deuda_id])
    .map((d) => ({
      cr_deuda_id: d.cr_deuda_id,
      periodo: d.periodo,
      remanente: Number(d.saldo) - (Number(elegidas[d.cr_deuda_id].monto) || 0)
    }))
    .filter((x) => x.remanente > 0)
)

const totalRemanente = computed(
  () => cuotasConRemanente.value.reduce((acc, x) => acc + x.remanente, 0)
)

// Se puede cobrar con cuotas, con productos, o con ambos: son los tres
// caminos de RN-34. Exigir cuotas dejaría al padre que sólo viene a comprar
// un uniforme sin pantalla donde hacerlo.
const hayAlgoQueCobrar = computed(
  () => cantidadElegidas.value > 0 || productos.value.length > 0
)

const desglose = computed(() => {
  const partes = []
  if (cantidadElegidas.value) {
    partes.push(`${cantidadElegidas.value} de ${pendientes.value.length} seleccionadas`)
  }
  if (productos.value.length) {
    const n = productos.value.length
    partes.push(`${n} producto${n === 1 ? '' : 's'}`)
  }
  return partes.join(' · ')
})

// RN-55 (D33). Ya no es un impedimento: es un aviso de que esa venta queda
// diferida. El operador tiene que saber que ese cobro NO produce factura ahora
// — si cree que sí, no va a entender por qué después aparece en lo pendiente.
const reciboConProductos = computed(
  () => documentoTipo.value === 'RECIBO' && productos.value.length > 0
)

const errorProductos = computed(() => {
  const mala = productos.value.find((p) => !(p.cantidad >= 1) || p.cantidad > p.stock)
  if (!mala) return ''
  return mala.cantidad > mala.stock
    ? `«${mala.nombre}»: sólo quedan ${mala.stock} en stock.`
    : `«${mala.nombre}»: la cantidad tiene que ser al menos 1.`
})

function buscarProducto () {
  clearTimeout(temporizadorProducto)
  if (busquedaProducto.value.length < 2) { resultadosProducto.value = []; return }
  temporizadorProducto = setTimeout(async () => {
    try {
      const encontrados = await buscarProductos(busquedaProducto.value)
      // Los ya agregados no se vuelven a ofrecer: agregarlos dos veces
      // produciría dos líneas del mismo producto y una factura confusa.
      const yaPuestos = new Set(productos.value.map((p) => p.producto_id))
      resultadosProducto.value = encontrados.filter((p) => !yaPuestos.has(p.producto_id))
    } catch {
      resultadosProducto.value = []
    }
  }, 250)
}

function agregarProducto (p) {
  if (!p.precio) {
    // Sin precio vigente el backend lo rechazaría con
    // `cobranza_producto_sin_precio`. Avisar acá evita que el operador arme
    // el cobro entero para que se lo rechacen al final.
    error.value = `«${p.nombre}» no tiene precio vigente: cargale uno en el catálogo antes de venderlo.`
    return
  }
  productos.value.push({ ...p, cantidad: 1 })
  busquedaProducto.value = ''
  resultadosProducto.value = []
  error.value = ''
}

// El monto de una línea no puede pasar el saldo ni ser cero. Se valida acá
// además del backend, para que el operador se entere antes de enviar — pero
// la garantía es el backend, que además tiene la deuda bloqueada (RN-26).
const errorDeLinea = (d) => {
  const elegida = elegidas[d.cr_deuda_id]
  if (!elegida) return ''
  const monto = Number(elegida.monto)
  if (!Number.isFinite(monto) || monto <= 0) return 'El monto tiene que ser mayor a cero.'
  if (monto > Number(d.saldo)) return `No podés cobrar más que el saldo (${formatearMonto(d.saldo)}).`
  return ''
}

const errorMontos = computed(() => {
  for (const d of pendientes.value) {
    const problema = errorDeLinea(d)
    if (problema) return `${d.descripcion}: ${problema}`
  }
  if (cantidadElegidas.value === 0) return ''
  return ''
})

const nombreDe = (s) =>
  s?.ficha?.cabecera?.nombre || `Suscripción #${s?.cr_suscripcion_id}`

// El identificador de la ficha (CI, RUC, matrícula, número de socio). Se
// muestra bajo el nombre porque es por lo que el operador la busca cuando dos
// fichas se llaman parecido.
const identificadorDe = (s) => s?.ficha?.cabecera?.id || ''

// CR-BC-003 · RB-3. Los resultados, agrupados por responsable.
//
// El agrupado mismo vive en `domain/cobros/busqueda.js` del backend y está
// probado ahí; acá se repite porque el desplegable es de esta pantalla y traer
// el grupo armado desde el API cambiaría el contrato de `/api/suscripciones`
// para todos sus consumidores. Es la misma regla en las dos puntas: agrupar por
// `responsable_cliente_id` conservando el orden recibido.
const grupos = computed(() => {
  const out = []
  const porClave = new Map()
  for (const s of resultados.value) {
    const id = s.responsable_cliente_id ?? null
    const clave = id === null ? '__sin__' : String(id)
    if (!porClave.has(clave)) {
      const g = {
        cliente_id: id,
        nombre: s.responsable_nombre || 'Sin responsable',
        documento: s.responsable_documento || null,
        fichas: []
      }
      porClave.set(clave, g)
      out.push(g)
    }
    porClave.get(clave).fichas.push(s)
  }
  return out
})

let temporizador = null
function buscar () {
  clearTimeout(temporizador)
  // RB-6. Con un solo carácter cualquiera de los cuatro criterios devuelve
  // medio padrón.
  if (busqueda.value.length < 2) { resultados.value = []; return }
  temporizador = setTimeout(async () => {
    try {
      // RB-1/RB-2: `busqueda` es el criterio ancho —documento o nombre del
      // responsable, nombre o identificador de la ficha—. `nombre` sigue
      // existiendo y significando lo de siempre para las otras pantallas.
      // AI-A2 (H-AI). `con_saldo` agrega las que no están activas pero todavía
      // deben algo. D6 conserva a propósito las cuotas con pagos aplicados al
      // dar de baja, así que pedir sólo `ACTIVA` dejaba deuda real sin forma
      // de cobrarse — y el operador terminó inscribiendo a la misma alumna por
      // tercera vez para poder cobrarla.
      resultados.value = await listarSuscripciones({
        estado: 'ACTIVA', con_saldo: 'true', busqueda: busqueda.value
      })
    } catch {
      resultados.value = []
    }
  }, 250)
}

async function elegir (s) {
  suscripcion.value = s
  documentoTipo.value = s.documento_preferido || 'FACTURA'
  resultados.value = []
  busqueda.value = ''
  // Cambiar de suscripción cambia el conjunto de quién puede pagar, así que
  // el pagador vuelve al responsable (RN-126). Arrastrarlo le facturaría a
  // alguien que no es titular de esta suscripción.
  await cargarCandidatos(s)
  // Cambiar de suscripción cambia al pagador: arrastrar los productos del
  // anterior se los facturaría a otra persona.
  productos.value = []
  busquedaProducto.value = ''
  resultadosProducto.value = []
  await cargarDeudas()
}

// Los titulares vigentes que pueden pagar esta suscripción.
//
// **El selector sólo aparece si hay más de uno** (RN-126): un desplegable de
// un elemento es ruido, y la línea de texto de siempre ya dice lo mismo.
//
// Si la llamada falla no se rompe el cobro: se queda sin selector y cobra el
// responsable, que es el comportamiento de antes de D43.
async function cargarCandidatos (s) {
  pagadorId.value = s?.responsable_cliente_id ?? null
  candidatos.value = []
  if (!s?.cr_suscripcion_id) return
  try {
    const detalle = await obtenerSuscripcion(s.cr_suscripcion_id)
    candidatos.value = candidatosAPagador(detalle?.titulares || [])
  } catch {
    candidatos.value = []
  }
}

async function cargarDeudas () {
  cargandoDeudas.value = true
  Object.keys(elegidas).forEach((k) => delete elegidas[k])
  try {
    deudas.value = await listarDeudas({ suscripcion_id: suscripcion.value.cr_suscripcion_id })
    // Todo lo vencido entra preseleccionado por su saldo completo: es lo que
    // el cajero va a cobrar en la mayoría de los casos.
    for (const d of pendientes.value) {
      if (d.vencida) elegidas[d.cr_deuda_id] = { monto: Number(d.saldo) }
    }
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron leer las cuotas.'
    deudas.value = []
  } finally {
    cargandoDeudas.value = false
  }
}

function alternar (d) {
  if (elegidas[d.cr_deuda_id]) delete elegidas[d.cr_deuda_id]
  else elegidas[d.cr_deuda_id] = { monto: Number(d.saldo) }
}

function alternarTodas () {
  if (todasElegidas.value) {
    Object.keys(elegidas).forEach((k) => delete elegidas[k])
  } else {
    for (const d of pendientes.value) elegidas[d.cr_deuda_id] = { monto: Number(d.saldo) }
  }
}

function limpiarSeleccion () {
  candidatos.value = []
  pagadorId.value = null
  suscripcion.value = null
  deudas.value = []
  Object.keys(elegidas).forEach((k) => delete elegidas[k])
  resultado.value = null
  error.value = ''
}

function reiniciar () {
  limpiarSeleccion()
  // Los productos también: si no, el uniforme del cobro anterior queda
  // cargado y el siguiente sale con una línea de más.
  productos.value = []
  busquedaProducto.value = ''
  resultadosProducto.value = []
  claveIdempotencia = nuevaClaveIdempotencia()
}

const emision = computed(() => estadoEmision(resultado.value))

// --- Imprimir el comprobante del cobro ------------------------------------
//
// El agente de impresión corre en la PC del operador (`pos-printer`, puerto
// 9977) y la llamada nace en el navegador, no en el backend — regla de
// integración con agentes locales de `AGENTS.md`.
//
// **Un fallo de impresión no invalida el cobro**: el dinero ya entró y la
// caja ya cuadra. Por eso el error se muestra al lado del botón y no como si
// el cobro hubiera fallado.
const imprimiendo = ref(false)
const errorImpresion = ref('')
// El cobro cuyo comprobante se está entregando (E10). Se pide por id al
// backend, igual que desde el listado: la pantalla no arma la URL.
const comprobanteDe = ref(null)

async function imprimirComprobante () {
  const movimientoId = resultado.value?.caja?.movimiento_id
  if (!movimientoId || imprimiendo.value) return

  imprimiendo.value = true
  errorImpresion.value = ''
  try {
    const settings = localPrintSettingsService.getSettings()
    if (!settings.enabled) {
      errorImpresion.value = 'La impresión local está desactivada. Activala en '
        + 'Mantenimiento › Operativo para imprimir desde esta pantalla.'
      return
    }
    await cashboxLocalPrintService.printMovementReceipt({ movementId: movimientoId, settings })
    notificaciones.notify({ type: 'success', message: 'Comprobante enviado a la impresora.' })
  } catch (err) {
    errorImpresion.value = err?.userMessage || err?.message
      || 'No se pudo imprimir el comprobante. El cobro quedó registrado igual.'
  } finally {
    imprimiendo.value = false
  }
}

// La caja quedo abierta sin salir del cobro: se limpia el error y el cajero
// vuelve a apretar Cobrar. NO se reintenta solo — el reintento automatico de
// una operacion de plata es exactamente lo que la clave de idempotencia esta
// para hacer explicito, no para esconder.
function alAbrirCaja () {
  cajaCerrada.value = false
  error.value = ''
  notificaciones.notify({ type: 'success', message: 'Caja abierta. Ya podés cobrar.' })
}

// Pasar a recibo es una decision del operador, no del sistema: se cambia el
// documento y **no se cobra solo**. Tiene que volver a apretar Cobrar, con las
// cuotas que ya habia marcado intactas.
function cobrarConRecibo () {
  documentoTipo.value = 'RECIBO'
  faltaIdentificacionFiscal.value = false
  error.value = ''
  notificaciones.notify({
    type: 'info',
    message: 'Cambiamos el documento a recibo de dinero. Revisá y apretá Cobrar.'
  })
}

async function confirmar () {
  error.value = ''
  cajaCerrada.value = false
  faltaIdentificacionFiscal.value = false

  if (!hayAlgoQueCobrar.value) {
    error.value = `Elegí al menos una ${vocabulario.value.singular.toLowerCase()} o un producto para cobrar.`
    return
  }
  if (errorMontos.value) { error.value = errorMontos.value; return }
  if (errorProductos.value) { error.value = errorProductos.value; return }


  cobrando.value = true
  try {
    resultado.value = await cobrar({
      idempotency_key: claveIdempotencia,
      forma_pago: formaPago.value,
      documento_tipo: documentoTipo.value,
      referencia_externa: referencia.value || null,
      nombre_pagador: nombrePagador.value,
      // `cliente_id` hace falta cuando NO hay cuotas: sin deuda de donde
      // sacar al responsable, el backend no sabe a nombre de quién emitir.
      cliente_id: suscripcion.value.responsable_cliente_id || null,
      // D43 — sólo cuando NO es el responsable. Mandarlo igual sería ruido, y
      // la base lo rechaza (RN-119).
      pagador_cliente_id: pagaOtro.value ? Number(pagadorId.value) : null,
      aplicaciones: Object.entries(elegidas).map(([id, e]) => ({
        cr_deuda_id: Number(id), monto: Number(e.monto)
      })),
      // Sólo id y cantidad: el precio lo pone el catálogo (RN-48, RP-9).
      productos: productos.value.map((p) => ({
        producto_id: p.producto_id, cantidad: Number(p.cantidad)
      }))
    })
    productos.value = []
    notificaciones.notify({ type: 'success', message: 'Cobro registrado.' })
    // La clave se renueva recién cuando el cobro salió bien: si falló, el
    // reintento tiene que llevar la MISMA clave.
    claveIdempotencia = nuevaClaveIdempotencia()
    await cargarDeudas()
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo registrar el cobro.'
    cajaCerrada.value = err?.normalized?.situation === 'caja_no_abierta'
    faltaIdentificacionFiscal.value =
      err?.normalized?.situation === 'cobranza_responsable_sin_identificacion_fiscal'
  } finally {
    cobrando.value = false
  }
}
</script>
